import {
  Box,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme
} from "@material-ui/core"
import NCText from "../../shared/NCText"
import NCTable from "../../shared/table/NCTable"
import { useEffect, useRef, useState } from "react"
import {
  SEARCH_CONFIG,
  TABLE_CONFIG
} from "../../libs/config"
import { useGetDriverList } from "../../services/queries"
import { SearchIcon } from "../../libs/icons"
import NCLoader from "../../shared/NCLoader"
import NCInput from "../../shared/NCInput"
import useDebounce from "../../libs/hooks/Debounce"
import NCTableStatus from "../../shared/table/NCTableStatus"
import Map from "../../components/map/Map"
import { useTranslation } from "react-i18next"
import NotificationAlert from "../../components/notification/NotificationAlert"
// import Filter from "../../shared/table/Filter"

const useStyles = makeStyles(() => ({
  headingContainer: {
    backgroundColor: "#F9FAFB",
    marginBottom: "10px"
  },
  name: {
    fontSize: "18px",
    fontWeight: 700,
    color: "rgba(16, 101, 142, 1)"
  },
  id: {
    fontSize: "18px",
    fontWeight: 400,
    color: "#444444"
  }
}))

const LiveLocation = () => {
  const cls = useStyles()

  const {t} = useTranslation();

  const ZOOM = 15

  const intervalRef = useRef()

  const mobileTheme = useTheme()
  const isSmallScreen = useMediaQuery(mobileTheme.breakpoints.down("sm"))
  const [selectedDriver, setSelectedDriver] = useState(null)
  const [stops, setStops] = useState([])
  const [center, setCenter] = useState({
    lat: 0,
    lng: 0
  })
  const [zoom, setZoom] = useState(ZOOM)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(TABLE_CONFIG.PAGE_SIZE)
  const [query, setQuery] = useState("")
  const [ordering, setOrdering] = useState("driver_name")
  const debouncedSearchTerm = useDebounce(query, SEARCH_CONFIG.TIME_IN_MILLI)
  const [statusFilter, setStatusFilter] = useState([])

  const { isLoading, data, refetch } = useGetDriverList(
    debouncedSearchTerm,
    page,
    pageSize,
    ordering,
    statusFilter && statusFilter.length
      ? statusFilter[0].value.toString()
      : null
  )

  const {
    next,
    previous,
    count,
    total_pages,
    results: driverData
  } = data?.data?.data || {}

  // const {
  //   data: selectedDriverData
  //   // refetch
  // } = useGetLiveLocation(selectedDriver?.id, {
  //   enabled: !!selectedDriver?.id
  // })

  const onNext = () => {
    setPage(prev => (next ? prev + 1 : prev))
  }
  const onPrev = () => {
    setPage(prev => (previous ? prev - 1 : prev))
  }

  // function SelectColumnFilter({ column: { filterValue, setFilter } }) {
  //   return (
  //     <Filter
  //       options={[
  //         { key: "1", name: "On a Trip/Ride" },
  //         { key: "2", name: "Idle" },
  //         { key: "3", name: "Offline" }
  //       ]}
  //       filters={filterValue}
  //       setFilters={setFilter}
  //     />
  //   )
  // }

  const columns = [
    {
      Header: t("list.driver_id"),
      id: "driver_id",
      accessor: cell => cell?.id,
      Cell: ({ value }) => (
        <Box pt={1} pb={1}>
          <NCText>{value}</NCText>
        </Box>
      )
    },
    {
      Header: t("list.driver_name"),
      id: "driver_name",
      accessor: cell => cell?.name
    },
    {
      Header: t("list.email"),
      accessor: cell => cell?.email,
      disableSortBy: true
    },
    {
      Header: t("list.phone_number"),
      accessor: cell => cell?.phone,
      disableSortBy: true
    },
    {
      Header: t("list.current_location"),
      accessor: cell => cell?.address,
      disableSortBy: true
    },
    {
      Header: t("list.status"),
      id: "status",
      accessor: cell => {
        const statusMap = {
          1: "Available",  // Green - matches "on a trip/ride"
          2: "Idle",            // Orange - matches "idle"
          3: "Offline"          // Red - matches "offline"
        }
        return (
          <NCTableStatus
            status={statusMap[cell?.driver_status] || "-"}
          />
        )
      },
      // defaultCanFilter: true,
      // Filter: SelectColumnFilter,
      disableSortBy: true
    }
  ]

  const onRowClicked = row => {
    const index = row.index
    const driver = driverData[index]
    setSelectedDriver(driver)
    const cordinates = {
      lat: driver?.latitude_current,
      lng: driver?.longitude_current
    }
    setCenter(cordinates)
    setZoom(ZOOM)
  }

  useEffect(() => {
    if (!selectedDriver) return;

    // Build popup content
    const info = (
      <div>
        <div className={cls.name}>{selectedDriver.name}</div>
        <div className={cls.id}>{selectedDriver.id}</div>
      </div>
    );

    // Create a single stop marker
    const stop = {
      latitude_current: selectedDriver.latitude_current,
      longitude_current: selectedDriver.longitude_current,
      blueMark: true,
      showPopup: true,
      info
    };

    setStops([stop]); // only one marker
    setCenter({
      lat: selectedDriver.latitude_current,
      lng: selectedDriver.longitude_current
    });
  }, [selectedDriver]);
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      refetch()
    }, [1000 * 30])

    return () => {
      clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(()=>{
    setSelectedDriver(null)
  },[page])

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Grid container justifyContent="space-between" mb={20}>
            <Grid item xs={12} sm={6} md={4}>
              <NCText variant={"h4"}>{t("live_location.heading")}</NCText>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={8}
              style={{
                marginTop: isSmallScreen ? "10px" : "0px"
              }}
            >
              <Grid
                container
                spacing={2}
                justifyContent={isSmallScreen ? "flex-start" : "flex-end"}
              >
                <Grid item>
                  <NCInput
                    value={query}
                    onChange={e => {
                      setPage(1)
                      setOrdering(null)
                      setQuery(e.target.value)
                    }}
                    placeholder={t("common.search_here")}
                    startIcon={SearchIcon}
                    className="rounded"
                  />
                </Grid>
                <Grid item style={{ paddingLeft: "0" }}>
                      <NotificationAlert />
                    </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box mb={2} pl={1} pr={1} mt={2} className={cls.headingContainer}>
          <Box
            pt={1}
            pb={1}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <NCText>
              {!selectedDriver
                ? t("live_location.no_driver_selected")
                : selectedDriver?.name +
                  " - " +
                  selectedDriver?.id +
                  " "}
              {selectedDriver && (
                <NCTableStatus
                  status={
                    selectedDriver?.driver_status === 1 ? "Available" :
                    selectedDriver?.driver_status === 2 ? "Idle" :
                    selectedDriver?.driver_status === 3 ? "Offline" : "-"
                  }
                />
              )}
            </NCText>
          </Box>
        </Box>
        <Box width={"100%"} height={"400px"}>
          <Map
            center={center}
            stops={stops ? false: false}
            showStartToEnd={false}
            showClientToDriver={false}
            zoomControl={true}
            zoom={zoom}
          />
        </Box>
        <Box mb={2} pl={1} pr={1} mt={2} className={cls.headingContainer}>
          <Box
            pt={1}
            pb={1}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <NCText>{t("live_location.drivers")}</NCText>
            <NCText style={{ fontWeight: "bold" }}>{count}</NCText>
          </Box>
        </Box>
        <NCTable
          columns={columns}
          data={driverData || []}
          page={page}
          onNext={onNext}
          onPrev={onPrev}
          pageCount={total_pages}
          gotoPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          sorting={ordering}
          setSorting={setOrdering}
          isLoading={isLoading}
          filter={statusFilter}
          setFilter={setStatusFilter}
          onRowClicked={onRowClicked}
          rowHightLight={true}
        />
        {isLoading && <NCLoader open={true} />}
      </Grid>
    </Grid>
  )
}

export default LiveLocation
